import { useEffect, useState } from "react";
import { X, AlertCircle, Loader2, Plus, Trash2, CheckCircle2, Clock, ShieldAlert, Edit2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Backdrop, Panel, Header, Footer, Field } from "./DepositModal";

export default function WhitelistManager({ onClose }) {
  const { user } = useAuth();
  const kycApproved = user?.kyc_status === "approved";

  const [activeTab, setActiveTab] = useState("deposit"); // "deposit" | "withdraw"
  const [assets, setAssets] = useState([]);
  const [depositWhitelist, setDepositWhitelist] = useState([]);
  const [withdrawWhitelist, setWithdrawWhitelist] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  // Form states for adding address
  const [asset, setAsset] = useState("");
  const [network, setNetwork] = useState("");
  const [address, setAddress] = useState("");
  const [memo, setMemo] = useState("");
  const [label, setLabel] = useState("");
  const [isOwnAddress, setIsOwnAddress] = useState(true);
  const [adding, setAdding] = useState(false);
  const [formError, setFormError] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);

  // Form states for changing deposit address
  const [changeTarget, setChangeTarget] = useState(null); // whitelist entry being changed
  const [newAddress, setNewAddress] = useState("");
  const [newMemo, setNewMemo] = useState("");
  const [changeIsOwnAddress, setChangeIsOwnAddress] = useState(true);
  const [totpCode, setTotpCode] = useState("");
  const [addTotpCode, setAddTotpCode] = useState("");
  const [changing, setChanging] = useState(false);
  const [changeError, setChangeError] = useState("");

  const [deletingId, setDeletingId] = useState(null);
  const [depositWlUnavailable, setDepositWlUnavailable] = useState(false);

  // Load assets
  useEffect(() => {
    api.get("/api/wallet/assets").then((r) => {
      const real = r.data.assets.filter((x) => !x.is_fiat);
      setAssets(real);
      if (real.length) {
        setAsset(real[0].symbol);
      }
    });
  }, []);

  // Auto-pick network when asset changes
  useEffect(() => {
    if (!asset || !assets.length) return;
    const a = assets.find((x) => x.symbol === asset);
    if (a && a.networks?.length) {
      setNetwork(a.networks[0].network);
    }
  }, [asset, assets]);

  // Load whitelists
  const loadLists = async () => {
    setLoadingList(true);
    try {
      const [depRes, withRes] = await Promise.all([
        api.get("/api/wallet/deposit-whitelist").catch((e) => {
          if (e?.response?.status === 404) {
            setDepositWlUnavailable(true);
          }
          return { data: { deposit_whitelist: [] } };
        }),
        api.get("/api/wallet/whitelist"),
      ]);
      setDepositWhitelist(depRes.data.deposit_whitelist || []);
      setWithdrawWhitelist(withRes.data.whitelist || []);
    } catch (e) {
      console.error("Failed to load whitelists", e);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadLists();
  }, []);

  const aSel = assets.find((x) => x.symbol === asset);

  // Handle Add Address
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!address || !label) return;
    setAdding(true);
    setFormError("");
    try {
      if (activeTab === "deposit") {
        await api.post("/api/wallet/deposit-whitelist", {
          asset,
          network,
          address,
          label,
          memo: memo || undefined,
          is_own_address: isOwnAddress,
        });
      } else {
        if (!addTotpCode) {
          setFormError("2FA code is required to submit a withdrawal address");
          return;
        }
        await api.post("/api/wallet/whitelist", {
          asset,
          network,
          address,
          label,
          memo: memo || undefined,
          totp_code: addTotpCode,
        });
      }
      // Reset form
      setAddress("");
      setMemo("");
      setLabel("");
      setAddTotpCode("");
      setIsOwnAddress(true);
      setShowAddForm(false);
      loadLists();
    } catch (err) {
      setFormError(err.response?.data?.detail || err.message || "Failed to add address");
    } finally {
      setAdding(false);
    }
  };

  // Handle Change Address
  const handleChangeAddressSubmit = async (e) => {
    e.preventDefault();
    if (!newAddress || !totpCode || !changeTarget) return;
    setChanging(true);
    setChangeError("");
    try {
      await api.post(`/api/wallet/deposit-whitelist/${changeTarget.id}/change`, {
        new_address: newAddress,
        new_memo: newMemo || undefined,
        is_own_address: changeIsOwnAddress,
        totp_code: totpCode,
      });
      // Reset
      setChangeTarget(null);
      setNewAddress("");
      setNewMemo("");
      setTotpCode("");
      loadLists();
    } catch (err) {
      setChangeError(err.response?.data?.detail || err.message || "Failed to change address");
    } finally {
      setChanging(false);
    }
  };

  // Handle Delete Address
  const handleDelete = async (id, type) => {
    setDeletingId(id);
    try {
      if (type === "deposit") {
        await api.delete(`/api/wallet/deposit-whitelist/${id}`);
      } else {
        await api.delete(`/api/wallet/whitelist/${id}`);
      }
      loadLists();
    } catch (err) {
      alert(err.response?.data?.detail || err.message || "Failed to delete address");
    } finally {
      setDeletingId(null);
    }
  };

  const getCoolingStatus = (item) => {
    if (activeTab === "withdraw") {
      const status = item.status || (item.verified ? "approved" : "pending_admin");
      if (status === "pending_admin") {
        return { active: false, text: "Pending admin approval" };
      }
      if (status === "rejected") {
        return { active: false, text: "Rejected by admin" };
      }
      if (item.is_usable) {
        return { active: true, text: "Active — ready for withdrawals" };
      }
      if (item.locked_until) {
        const now = new Date();
        const until = new Date(item.locked_until);
        if (until > now) {
          const diffHrs = Math.ceil((until - now) / (1000 * 60 * 60));
          return { active: false, text: `Approved — cooling (${diffHrs}h remaining)` };
        }
      }
      return { active: true, text: "Active" };
    }
    const status = item.status || (item.is_active ? "active" : "cooling");
    if (status === "pending_admin") {
      return { active: false, text: "Pending admin approval" };
    }
    if (status === "rejected") {
      return { active: false, text: "Rejected by admin" };
    }
    if (item.is_active) {
      return { active: true, text: "Active — held deposits released" };
    }
    const until = item.cooling_until ? new Date(item.cooling_until) : null;
    if (until && until > new Date()) {
      const diffHrs = Math.ceil((until - new Date()) / (1000 * 60 * 60));
      return { active: false, text: `Approved — cooling (${diffHrs}h remaining)` };
    }
    return { active: false, text: "Awaiting activation" };
  };

  const currentList = activeTab === "deposit" ? depositWhitelist : withdrawWhitelist;

  return (
    <Backdrop onClose={() => onClose(true)}>
      <Panel>
        <Header title="Whitelist Manager" onClose={() => onClose(true)} testId="whitelist-close" />
        
        {/* Tab Controls */}
        <div className="flex border-b border-[var(--cm-border)]">
          <button
            onClick={() => {
              setActiveTab("deposit");
              setShowAddForm(false);
              setChangeTarget(null);
            }}
            className={`flex-1 py-3 text-center text-[14px] font-semibold transition-colors border-b-2 ${
              activeTab === "deposit"
                ? "border-cm-blue text-cm-blue"
                : "border-transparent text-cm-grey hover:text-cm-navy"
            }`}
            data-testid="whitelist-tab-deposit"
          >
            Deposit Whitelist
          </button>
          <button
            onClick={() => {
              setActiveTab("withdraw");
              setShowAddForm(false);
              setChangeTarget(null);
            }}
            className={`flex-1 py-3 text-center text-[14px] font-semibold transition-colors border-b-2 ${
              activeTab === "withdraw"
                ? "border-cm-blue text-cm-blue"
                : "border-transparent text-cm-grey hover:text-cm-navy"
            }`}
            data-testid="whitelist-tab-withdraw"
          >
            Withdrawal Whitelist
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Deposit unavailable notice */}
          {activeTab === "deposit" && depositWlUnavailable && (
            <div className="rounded-[12px] bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
              <ShieldAlert className="text-amber-600 mt-0.5 shrink-0" size={20} />
              <div>
                <p className="font-bold text-amber-900 text-[13px]">Deposit whitelist unavailable</p>
                <p className="text-amber-700 text-[12px] mt-1 leading-relaxed">
                  The server does not have deposit whitelist support yet. The backend needs to be updated.
                  Withdrawal whitelist still works — switch to the Withdrawal tab.
                </p>
              </div>
            </div>
          )}
          {/* KYC Gate */}
          {!kycApproved ? (
            <div className="rounded-[12px] bg-amber-50 border border-amber-200 p-5 text-center space-y-3">
              <ShieldAlert className="mx-auto text-amber-600" size={36} />
              <div>
                <h4 className="font-bold text-amber-900 text-[15px]">KYC Verification Required</h4>
                <p className="text-amber-700 text-[12px] mt-1">
                  You must complete and get approved for KYC verification to manage your whitelisted addresses.
                </p>
              </div>
              <button
                onClick={() => {
                  window.location.href = "/app/settings";
                }}
                className="px-4 py-2 rounded-[8px] bg-amber-600 hover:bg-amber-700 text-white text-[12px] font-semibold inline-block"
              >
                Go to Verification
              </button>
            </div>
          ) : changeTarget ? (
            /* Change Address Form */
            <form onSubmit={handleChangeAddressSubmit} className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-bold text-cm-navy text-[14px]">
                  Change Address: {changeTarget.label} ({changeTarget.asset})
                </h4>
                <button
                  type="button"
                  onClick={() => setChangeTarget(null)}
                  className="text-cm-grey hover:text-cm-navy text-[12px] font-semibold"
                >
                  Cancel
                </button>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-[10px] p-3 text-[12px] text-amber-800">
                Changing a whitelisted deposit address triggers a new cooling period (24h for own, 48h for third-party). 2FA verification is required.
              </div>

              <Field label="New Address">
                <input
                  type="text"
                  required
                  value={newAddress}
                  onChange={(e) => setNewAddress(e.target.value.trim())}
                  placeholder={`New ${changeTarget.network} address`}
                  className="w-full px-3 py-2 rounded-[8px] border border-[var(--cm-border)] text-[13px] font-mono"
                  data-testid="whitelist-change-address-input"
                />
              </Field>

              {changeTarget.asset === "XRP" && (
                <Field label="New Tag/Memo">
                  <input
                    type="text"
                    value={newMemo}
                    onChange={(e) => setNewMemo(e.target.value.trim())}
                    placeholder="Optional memo"
                    className="w-full px-3 py-2 rounded-[8px] border border-[var(--cm-border)] text-[13px] font-mono"
                  />
                </Field>
              )}

              <label className="flex items-center gap-2 text-[13px] text-cm-navy cursor-pointer">
                <input
                  type="checkbox"
                  checked={changeIsOwnAddress}
                  onChange={(e) => setChangeIsOwnAddress(e.target.checked)}
                />
                This address belongs to me (24h cooling vs 48h for others)
              </label>

              <Field label="2FA Authenticator Code">
                <input
                  type="text"
                  required
                  inputMode="numeric"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  className="w-full px-3 py-2 rounded-[8px] border border-[var(--cm-border)] text-[13px] text-center font-mono tracking-widest"
                  data-testid="whitelist-change-totp"
                />
              </Field>

              {changeError && (
                <div className="flex items-start gap-2 p-3 rounded-[8px] bg-red-50 text-red-700 text-[12px]">
                  <AlertCircle size={14} className="mt-0.5" /> {changeError}
                </div>
              )}

              <button
                type="submit"
                disabled={changing || !newAddress || totpCode.length < 6}
                className="w-full py-2.5 rounded-[10px] bg-cm-navy text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="whitelist-change-submit"
              >
                {changing && <Loader2 size={14} className="animate-spin" />}
                Confirm Address Change
              </button>
            </form>
          ) : (
            /* Main List & Add Layout */
            <>
              {/* Add form toggle — hidden when deposit is unavailable */}
              {!(activeTab === "deposit" && depositWlUnavailable) && !showAddForm ? (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="w-full py-2.5 rounded-[10px] border border-dashed border-cm-blue text-cm-blue text-[13px] font-semibold hover:bg-blue-50/50 flex items-center justify-center gap-1.5"
                  data-testid="whitelist-add-toggle"
                >
                  <Plus size={15} /> Add Whitelist Address
                </button>
              ) : (
                <form onSubmit={handleAdd} className="border border-[var(--cm-border)] rounded-[12px] p-4 space-y-4 bg-cm-surface">
                  <div className="flex items-center justify-between">
                    <h4 className="font-bold text-cm-navy text-[13px]">Add {activeTab} Address</h4>
                    <button
                      type="button"
                      onClick={() => setShowAddForm(false)}
                      className="text-cm-grey hover:text-cm-navy text-[11px]"
                    >
                      Hide Form
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Asset">
                      <select
                        value={asset}
                        onChange={(e) => setAsset(e.target.value)}
                        className="w-full px-3 py-2 rounded-[8px] border border-[var(--cm-border)] bg-white text-[13px]"
                        data-testid="whitelist-add-asset-select"
                      >
                        {assets.map((a) => (
                          <option key={a.symbol} value={a.symbol}>
                            {a.symbol}
                          </option>
                        ))}
                      </select>
                    </Field>

                    {aSel?.networks?.length > 0 && (
                      <Field label="Network">
                        <select
                          value={network}
                          onChange={(e) => setNetwork(e.target.value)}
                          className="w-full px-3 py-2 rounded-[8px] border border-[var(--cm-border)] bg-white text-[13px]"
                        >
                          {aSel.networks.map((n) => (
                            <option key={n.network} value={n.network}>
                              {n.network}
                            </option>
                          ))}
                        </select>
                      </Field>
                    )}
                  </div>

                  <Field label="Address">
                    <input
                      type="text"
                      required
                      value={address}
                      onChange={(e) => setAddress(e.target.value.trim())}
                      placeholder="Destination wallet address"
                      className="w-full px-3 py-2 rounded-[8px] border border-[var(--cm-border)] text-[13px] font-mono"
                      data-testid="whitelist-add-address-input"
                    />
                  </Field>

                  {aSel?.needs_memo && (
                    <Field label="Tag/Memo (Required for XRP)">
                      <input
                        type="text"
                        value={memo}
                        onChange={(e) => setMemo(e.target.value.trim())}
                        placeholder="Tag or memo code"
                        className="w-full px-3 py-2 rounded-[8px] border border-[var(--cm-border)] text-[13px] font-mono"
                      />
                    </Field>
                  )}

                  <Field label="Label / Description">
                    <input
                      type="text"
                      required
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="e.g. My Ledger Wallet"
                      className="w-full px-3 py-2 rounded-[8px] border border-[var(--cm-border)] text-[13px]"
                      data-testid="whitelist-add-label-input"
                    />
                  </Field>

                  {activeTab === "deposit" && (
                    <label className="flex items-center gap-2 text-[13px] text-cm-navy cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isOwnAddress}
                        onChange={(e) => setIsOwnAddress(e.target.checked)}
                      />
                      This is my own address
                    </label>
                  )}

                  {activeTab === "withdraw" && (
                    <>
                      <div className="bg-blue-50 border border-blue-200 rounded-[10px] p-3 text-[12px] text-blue-900">
                        Withdrawal addresses require <b>admin approval</b>, then a 24h cooldown before use.
                      </div>
                      <Field label="2FA Code (Authenticator)">
                        <input
                          type="text"
                          inputMode="numeric"
                          required
                          value={addTotpCode}
                          onChange={(e) => setAddTotpCode(e.target.value.trim())}
                          placeholder="6-digit code"
                          className="w-full px-3 py-2 rounded-[8px] border border-[var(--cm-border)] text-[13px] font-mono"
                          data-testid="whitelist-add-totp"
                        />
                      </Field>
                    </>
                  )}

                  {formError && (
                    <div className="flex items-start gap-2 p-3 rounded-[8px] bg-red-50 text-red-700 text-[12px]">
                      <AlertCircle size={14} className="mt-0.5" /> {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={adding || !address || !label || (activeTab === "withdraw" && !addTotpCode)}
                    className="w-full py-2 rounded-[8px] bg-cm-blue hover:bg-cm-blue-deep active:bg-cm-blue-deep text-white text-[13px] font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5"
                    data-testid="whitelist-add-submit"
                  >
                    {adding && <Loader2 size={13} className="animate-spin" />}
                    Add Address to Whitelist
                  </button>
                </form>
              )}

              {/* List of Whitelisted Addresses */}
              <div className="space-y-3 mt-4">
                <h4 className="font-bold text-cm-navy text-[13px] flex items-center gap-2">
                  Whitelisted Addresses ({currentList.length})
                  {loadingList && <Loader2 size={13} className="animate-spin text-cm-grey" />}
                </h4>

                {currentList.length === 0 ? (
                  <div className="py-8 text-center text-cm-grey text-[13px] border border-dashed border-[var(--cm-border)] rounded-[12px]">
                    No whitelisted addresses.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {currentList.map((item) => {
                      const status = getCoolingStatus(item);
                      return (
                        <div
                          key={item.id}
                          className="p-3 border border-[var(--cm-border)] rounded-[12px] flex items-center justify-between gap-4 hover:bg-cm-surface/30 transition-colors"
                          data-testid={`whitelist-item-${item.id}`}
                        >
                          <div className="space-y-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-cm-navy text-[13px]">
                                {item.label}
                              </span>
                              <span className="px-1.5 py-0.5 rounded bg-cm-surface text-[10px] font-semibold text-cm-navy">
                                {item.asset} ({item.network})
                              </span>
                              {activeTab === "deposit" && (
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                  item.is_own_address ? "bg-blue-50 text-cm-blue" : "bg-purple-50 text-purple-700"
                                }`}>
                                  {item.is_own_address ? "Own" : "Third-Party"}
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] font-mono text-cm-grey truncate max-w-[280px]">
                              {item.address}
                              {item.memo && <span className="text-amber-700 ml-1">Memo: {item.memo}</span>}
                            </div>
                            <div className="flex items-center gap-1.5 text-[11px]">
                              {status.active ? (
                                <span className="text-emerald-600 flex items-center gap-1 font-semibold">
                                  <CheckCircle2 size={12} /> {status.text}
                                </span>
                              ) : (
                                <span className="text-amber-600 flex items-center gap-1 font-semibold">
                                  <Clock size={12} /> {status.text}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            {activeTab === "deposit" && (
                              <button
                                onClick={() => {
                                  setChangeTarget(item);
                                  setNewAddress(item.address);
                                  setNewMemo(item.memo || "");
                                  setChangeIsOwnAddress(item.is_own_address);
                                  setTotpCode("");
                                  setChangeError("");
                                }}
                                className="p-1.5 rounded hover:bg-cm-surface text-cm-grey hover:text-cm-navy transition-colors"
                                title="Change address"
                                data-testid={`whitelist-item-change-${item.id}`}
                              >
                                <Edit2 size={14} />
                              </button>
                            )}
                            <button
                              disabled={deletingId === item.id}
                              onClick={() => handleDelete(item.id, activeTab)}
                              className="p-1.5 rounded hover:bg-red-50 text-cm-grey hover:text-red-600 transition-colors"
                              title="Delete address"
                              data-testid={`whitelist-item-delete-${item.id}`}
                            >
                              {deletingId === item.id ? (
                                <Loader2 size={14} className="animate-spin text-red-600" />
                              ) : (
                                <Trash2 size={14} />
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              </>
          )}
        </div>

        <Footer>
          <button
            onClick={() => onClose(true)}
            className="px-5 py-2 rounded-[10px] bg-cm-blue text-white text-[13px] font-semibold"
            data-testid="whitelist-done"
          >
            Done
          </button>
        </Footer>
      </Panel>
    </Backdrop>
  );
}
